// Canonical, language-neutral codes for alarm dropdown enums.
//
// BACKGROUND: historically these dropdowns stored the *translated label*
// (e.g. "Nödlarm" / "Nødalarm" / "Emergency alarm") as the persisted value.
// That broke cross-language usage (a Swedish value saved in the Norwegian
// portal becomes invisible when the UI language is Norwegian, etc.).
//
// FIX: dropdowns now save these stable codes and translate for display.
// Existing DB rows still hold legacy labels, so every place that READS the
// value runs it through the normalizers below (normalize-on-read — no DB
// migration required). The legacy data is messy (typos, mixed languages,
// case differences), so matching is heuristic/substring based rather than an
// exact lookup table.

import { isNorwegianSite } from '@/utils/siteContext';
import { isDutchCustomer } from '@/utils/countryOrigin';

export const RISK_PROFILE = {
  THREAT: 'threat',
  EMERGENCY: 'emergency',
  ASSAULT: 'assault',
};

export const ALARM_SORT = {
  GROUP: 'group',
  PERSONAL: 'personal',
};

const RISK_PROFILE_CODES = new Set(Object.values(RISK_PROFILE));
const ALARM_SORT_CODES = new Set(Object.values(ALARM_SORT));

// Canonical i18n keys used when DISPLAYING a code as a translated label.
// (Dropdown option labels may use other namespaces per form; these are the
// keys used for read-only display of a stored value.)
export const RISK_PROFILE_I18N = {
  [RISK_PROFILE.THREAT]: 'risk_profile.threat_alarm',
  [RISK_PROFILE.EMERGENCY]: 'risk_profile.emergency_alarm',
  [RISK_PROFILE.ASSAULT]: 'risk_profile.burglar_alarm',
};

export const ALARM_SORT_I18N = {
  [ALARM_SORT.GROUP]: 'alarm_create.group_alarm',
  [ALARM_SORT.PERSONAL]: 'alarm_create.personal',
};

/**
 * Map any stored risk_profile value (legacy sv/no/en label OR a code) to a
 * canonical code. Unknown values — notably the Malmö special cases, which are
 * handled separately elsewhere — pass through unchanged.
 */
export function normalizeRiskProfile(value) {
  if (value === null || value === undefined || value === '') return value;
  if (RISK_PROFILE_CODES.has(value)) return value;

  const s = String(value).toLowerCase();

  // Malmö profiles are intentionally left as-is (handled as a special case).
  if (s.includes('malmö') || s.includes('malmo')) return value;

  if (s.includes('hot') || s.includes('trussel') || s.includes('trusel') || s.includes('threat')) {
    return RISK_PROFILE.THREAT;
  }
  if (s.includes('nöd') || s.includes('nød') || s.includes('emergency')) {
    return RISK_PROFILE.EMERGENCY;
  }
  // 'overfals' = the misspelt Norwegian label "Overfalslarm (ikke tilbakekallbart)"
  if (s.includes('överfall') || s.includes('overfall') || s.includes('overfals') || s.includes('assault') || s.includes('burglar')) {
    return RISK_PROFILE.ASSAULT;
  }
  // Polish labels (legacy alarms created on the pl locale)
  if (s.includes('zagro')) return RISK_PROFILE.THREAT;      // "Alarm zagrożenia"
  if (s.includes('awaryj')) return RISK_PROFILE.EMERGENCY;  // "Alarm awaryjny"
  if (s.includes('napa')) return RISK_PROFILE.ASSAULT;      // "Alarm napaści ..."
  // Dutch labels (the nl locale, SEC-348) - the forms store the code, but a
  // label is converted like any other language's (SEC-373)
  if (s.includes('dreig')) return RISK_PROFILE.THREAT;      // "Dreigingsalarm"
  if (s.includes('nood')) return RISK_PROFILE.EMERGENCY;    // "Noodalarm"
  if (s.includes('overval')) return RISK_PROFILE.ASSAULT;   // "Overvalalarm (niet herroepbaar)"

  return value; // unknown — leave untouched
}

/**
 * Map any stored selected_alarm_sort value (legacy sv/no/en label OR a code)
 * to a canonical code.
 */
export function normalizeAlarmSort(value) {
  if (value === null || value === undefined || value === '') return value;
  if (ALARM_SORT_CODES.has(value)) return value;

  const s = String(value).toLowerCase();

  if (s.includes('grup') || s.includes('group')) return ALARM_SORT.GROUP; // sv/no "Grupp...", pl "grupowy/grupa"
  // "Personligt"/"Personlig"/"Personlarm"/"Personal"/"Staff"/"Personale"/pl "Osobiste" —
  // within this field the only non-group option is the personal alarm.
  if (s.includes('person') || s.includes('staff') || s.includes('osob')) return ALARM_SORT.PERSONAL;

  return value; // unknown — leave untouched
}

/**
 * Translate a stored risk_profile value (code or legacy label) to a label in
 * the current language. Malmö / unknown values render as their raw stored text.
 * @param {function} t  a translator, e.g. this.$t or the PDF's locale-bound t
 */
export function riskProfileLabel(t, value) {
  const code = normalizeRiskProfile(value);
  const key = RISK_PROFILE_I18N[code];
  return key ? t(key) : (value || '');
}

/**
 * The alarm-profile texts of the alarm plan ("Primär insats" and "Åtgärd vid
 * larm") for a stored risk_profile value. The PDF prints these and the device
 * forms preview them - both read them here so the two always say the same.
 * Anything that is not emergency or assault (including the Malmö profiles)
 * gets the threat texts, as the PDF always has.
 */
export function riskProfileTexts(t, value) {
  const code = normalizeRiskProfile(value);
  if (code === RISK_PROFILE.EMERGENCY) return { title: t('alarm_pdf.risk_title_2'), desc: t('alarm_pdf.risk_desc_2') };
  if (code === RISK_PROFILE.ASSAULT) return { title: t('alarm_pdf.risk_title_3'), desc: t('alarm_pdf.risk_desc_3') };
  return { title: t('alarm_pdf.risk_title'), desc: t('alarm_pdf.risk_desc') };
}

/**
 * The language the alarm plan is written in - the PDF and the device forms'
 * preview both read it here. It follows the site, never the user's UI language:
 * Norwegian on the Norwegian portal, Swedish on the Swedish one - except for a
 * Dutch customer's devices (country_origin 'nl'), whose plan is in Dutch.
 * @param {object} customer  the customer the alarm's business (Unit) belongs to
 */
export function alarmPlanLocale(customer) {
  if (isNorwegianSite()) return 'no';
  return isDutchCustomer(customer) ? 'nl' : 'sv';
}

/**
 * The customer-specific instruction of a business (Unit), or '' when it has
 * none. When set, the alarm plan prints it instead of riskProfileTexts - the
 * PDF and the device forms' preview both read it here. Units saved before the
 * field existed have no value, which means "off".
 */
export function customInstructionText(unit) {
  if (!unit || unit.custom_instruction_enabled !== true || typeof unit.custom_instruction !== 'string') return '';
  return unit.custom_instruction.trim();
}

/**
 * Translate a stored selected_alarm_sort value (code or legacy label) to a
 * label in the current language.
 */
export function alarmSortLabel(t, value) {
  const code = normalizeAlarmSort(value);
  const key = ALARM_SORT_I18N[code];
  return key ? t(key) : (value || '');
}